import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Trash2, RotateCcw, Video, Loader2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// Placeholder for deleted videos (would come from API)
const mockDeletedVideos = [
  { id: '1', title: 'Product Launch Video', thumbnailUrl: 'https://picsum.photos/seed/video1/400/225', deletedAt: Date.now() - 86400000, duration: '1:24' },
  { id: '2', title: 'Team Introduction', thumbnailUrl: 'https://picsum.photos/seed/video2/400/225', deletedAt: Date.now() - 172800000, duration: '2:15' },
  { id: '3', title: 'Feature Walkthrough', thumbnailUrl: 'https://picsum.photos/seed/video3/400/225', deletedAt: Date.now() - 345600000, duration: '3:42' },
]

export default function TrashPage() {
  const [deletedVideos] = useState(mockDeletedVideos)
  const [selectedVideos, setSelectedVideos] = useState<string[]>([])
  const [isLoading] = useState(false)

  const toggleSelect = (id: string) => {
    setSelectedVideos((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const formatDate = (timestamp: number) => {
    const days = Math.floor((Date.now() - timestamp) / 86400000)
    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    return `${days} days ago`
  }

  // Calculate days until permanent deletion (30 days total)
  const getDaysRemaining = (deletedAt: number) => {
    const daysElapsed = Math.floor((Date.now() - deletedAt) / 86400000)
    return Math.max(0, 30 - daysElapsed)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    )
  }

  return (
    <div className="p-6">
      <Helmet>
        <title>Trash - Footage</title>
        <meta name="description" content="View and restore deleted videos." />
      </Helmet>
      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Trash</h1>
            <p className="text-sm text-neutral-500 mt-1">
              Deleted videos are kept for 30 days before permanent removal.
            </p>
          </div>
          {selectedVideos.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-500">
                {selectedVideos.length} selected
              </span>
              <Button variant="outline" size="sm">
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Restore
              </Button>
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete Forever
              </Button>
            </div>
          )}
        </div>

        {deletedVideos.length === 0 ? (
          <div className="border-2 border-dashed border-neutral-200 rounded-xl p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="h-8 w-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 mb-1">Trash is empty</h3>
            <p className="text-sm text-neutral-500 max-w-sm mx-auto">
              Videos you delete will appear here. They'll be permanently removed after 30 days.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {deletedVideos.map((video) => {
              const daysRemaining = getDaysRemaining(video.deletedAt)
              const isExpiringSoon = daysRemaining <= 7

              return (
                <div
                  key={video.id}
                  onClick={() => toggleSelect(video.id)}
                  className={cn(
                    'flex items-center gap-4 p-3 rounded-xl border-2 cursor-pointer transition-all',
                    selectedVideos.includes(video.id)
                      ? 'border-teal-500 bg-teal-50/50'
                      : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                  )}
                >
                  {/* Checkbox */}
                  <div
                    className={cn(
                      'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all',
                      selectedVideos.includes(video.id)
                        ? 'bg-teal-500 border-teal-500'
                        : 'bg-white border-neutral-300'
                    )}
                  >
                    {selectedVideos.includes(video.id) && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>

                  {/* Thumbnail */}
                  <div className="relative w-32 aspect-video rounded-lg overflow-hidden bg-neutral-100 shrink-0">
                    {video.thumbnailUrl ? (
                      <img
                        src={video.thumbnailUrl}
                        alt={video.title}
                        className="w-full h-full object-cover opacity-75"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="h-6 w-6 text-neutral-400" />
                      </div>
                    )}
                    <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-xs text-white">
                      {video.duration}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">{video.title}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      Deleted {formatDate(video.deletedAt)}
                    </p>
                  </div>

                  {/* Expiry warning */}
                  <div className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0',
                    isExpiringSoon
                      ? 'bg-red-50 text-red-600'
                      : 'bg-neutral-100 text-neutral-600'
                  )}>
                    {isExpiringSoon && <AlertTriangle className="h-3 w-3" />}
                    {daysRemaining} days left
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
